import React, { useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { ForgotPasswordRequest } from '@/types/Employee.type';
import URL from '@/common/routes/Url';
import { useAuth } from '@/common/hooks/useAuth';
import { Grid, Link } from '@mui/material';
import Copyright from '@/components/common/Copyright';
import HookFormControls, { HookFormControlsProps } from '@/components/common/HookFormControls';
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';

const ForgotPassword: React.FC = () => {
  const auth = useAuth();
  const useFormMethods = useForm<ForgotPasswordRequest>({ shouldUnregister: false });
  const { handleSubmit } = useFormMethods;
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const email: HookFormControlsProps = {
    name: 'email',
    type: 'text',
    label: 'メールアドレス*',
    helper:
      'ログインに使用するメールアドレスを入力してください。パスワードをリセットするための認証コードがメールで送信されます。',
    rule: {
      required: 'メールアドレスは必須項目です。',
      maxLength: {
        value: 255,
        message: 'メールアドレスは255文字以内で入力してください。',
      },
      pattern: {
        value: new RegExp(
          "^[a-zA-Z0-9\\.\\!\\#\\$\\%\\&\\'\\*\\+\\/\\=\\\\\\?\\^\\_\\`\\{\\|\\}\\~\\-]+\\@[a-zA-Z0-9-]+(?:\\.[a-zA-Z0-9-]+)*$",
        ),
        message: 'メールアドレスの形式が不正です。',
      },
    },
  };

  useLayoutEffect(() => {
    auth.signOut();
  }, []);

  const onSubmit = async (values: ForgotPasswordRequest) => {
    try {
      const result = await auth.forgotPassword(values.email);
      if (result) {
        enqueueSnackbar('認証コードを送信しました。', { variant: 'info' });
        navigate(`${URL.resetPassword}`, { state: { email: values.email } });
        return;
      }
      enqueueSnackbar('認証コードの送信に失敗しました。');
    } catch (error: any) {
      enqueueSnackbar('認証コードの送信に失敗しました。');
      return;
    }
  };

  return (
    <Grid container justifyContent="center" sx={{ mt: 12 }}>
      <Grid item xs={12} md={8} lg={4} sx={{ mx: 4 }}>
        <FormProvider {...useFormMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <HookFormControls {...email} autoComplete="off" />
            <LoadingButton type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }} loading={auth.isLoading}>
              送信
            </LoadingButton>
          </form>
        </FormProvider>

        <Link
          variant="body1"
          display="block"
          align="center"
          onClick={() => {
            navigate(`${URL.resetPassword}`);
          }}
        >
          受け取った認証コードを入力し、新しいパスワードを登録してください。
        </Link>
        <Copyright />
      </Grid>
    </Grid>
  );
};
export default ForgotPassword;
